import { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import type { Rating } from '@xphoto/shared';
import { rovingRadioTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { ApiError } from '../gallery/api';
import { setRating as setRatingApi } from './viewer-api';

/**
 * Contrôle de NOTE (rating 0..5) de la barre d'édition de la visionneuse (LOT 2).
 *
 * - 5 étoiles cliquables ; cliquer l'étoile `n` fixe la note à `n`, RE-cliquer
 *   l'étoile de la note courante la remet à `0` (« non noté »).
 * - Persistance OPTIMISTE via `PATCH /api/assets/:id/rating` ; en cas d'échec on
 *   REVERT et on affiche un message non bloquant (403 = lecture seule → message
 *   dédié, jamais de crash).
 * - a11y : `role=radiogroup` (0 = non noté), chaque étoile est un `radio` avec un
 *   `aria-label` explicite ; l'état plein/vide est porté par la FORME (étoile
 *   pleine `fill` vs contour) ET par `aria-checked` — jamais la couleur seule.
 * - clavier : flèches + `tabIndex` mobile (`rovingFocus.ts`) en ACTIVATION MANUELLE.
 *   Seul radiogroup du lot à diverger du motif ARIA standard, et pour une raison
 *   nette : chaque changement ÉCRIT IMMÉDIATEMENT la note en base (`PATCH`). Faire
 *   suivre la sélection au focus émettrait une requête PAR FLÈCHE, et l'utilisateur
 *   qui traverse les étoiles laisserait derrière lui quatre notes fausses. Les flèches
 *   déplacent donc le focus ; Entrée/Espace valident.
 */
interface RatingControlProps {
  assetId: string;
  rating: Rating;
  /** Répercute la nouvelle note (grille + détail). */
  onRatingChange: (rating: Rating) => void;
}

const STARS: readonly Rating[] = [1, 2, 3, 4, 5];

export function RatingControl({
  assetId,
  rating,
  onRatingChange,
}: RatingControlProps): React.JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'generic' | 'readonly' | null>(null);
  const onStarsKeyDown = useRovingRadioGroup('manual');

  const apply = async (next: Rating): Promise<void> => {
    if (busy) return;
    const previous = rating;
    setBusy(true);
    setError(null);
    // Optimiste : on applique tout de suite, on annule en cas d'échec.
    onRatingChange(next);
    try {
      await setRatingApi(assetId, next);
    } catch (err) {
      onRatingChange(previous);
      setError(err instanceof ApiError && err.status === 403 ? 'readonly' : 'generic');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div
          role="radiogroup"
          aria-label={t('rating.label')}
          onKeyDown={onStarsKeyDown}
          className="inline-flex items-center gap-0.5"
        >
          {STARS.map((n) => {
            const filled = n <= rating;
            // Re-cliquer l'étoile de la note courante remet à 0.
            const target: Rating = n === rating ? 0 : n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={n === rating}
                // Note 0 (« non noté ») : aucune étoile cochée → la 1re est le point d'entrée.
                tabIndex={rovingRadioTabIndex(n === rating, n === STARS[0], rating > 0)}
                disabled={busy}
                onClick={() => void apply(target)}
                aria-label={interpolate(t('rating.star'), { n })}
                title={interpolate(t('rating.star'), { n })}
                // 44×44 px (WCAG 2.5.8) : `p-0.5` donnait 28 px pour CINQ boutons
                // collés, alors que le clic ÉCRIT IMMÉDIATEMENT la note sans
                // confirmation — une erreur de frappe attribuait la mauvaise note.
                className="flex h-11 w-11 items-center justify-center rounded text-white hover:text-accent-on-dark disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <StarIcon
                  className="h-6 w-6"
                  fill={filled ? 'currentColor' : 'none'}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
        {rating > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void apply(0)}
            className="inline-flex min-h-11 items-center rounded-md px-3 py-1 text-xs text-white/80 hover:bg-white/10 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {t('rating.clear')}
          </button>
        )}
      </div>
      {error !== null && (
        <span role="alert" className="text-xs text-danger">
          {error === 'readonly' ? t('rating.errorReadonly') : t('rating.error')}
        </span>
      )}
    </div>
  );
}
